import { useMediaQuery } from '@ori-ui/mui';
import type { RefObject, ElementType } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export const useHover = <Ref extends HTMLElement = HTMLAnchorElement & HTMLButtonElement>(
  iconOnHover: ElementType,
  icon: ElementType,
  hidden?: boolean,
): [RefObject<Ref>, ElementType] => {
  const [isHover, setIsHover] = useState(false);
  const ref = useRef<Ref>(null);
  const isHoverAvailable = useMediaQuery('(hover: hover)');

  const getIcon = useMemo(() => {
    if (isHover) {
      return iconOnHover;
    }

    return icon;
  }, [isHover, icon, iconOnHover]);

  const handleMouseOver = useCallback(() => {
    setIsHover(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsHover(false);
  }, []);

  useEffect(() => {
    const element = ref.current;

    if (isHoverAvailable && !hidden) {
      element?.addEventListener('mouseover', handleMouseOver);
      element?.addEventListener('mouseleave', handleMouseLeave);
    }

    return () => {
      if (isHoverAvailable && !hidden) {
        element?.removeEventListener('mouseover', handleMouseOver);
        element?.removeEventListener('mouseleave', handleMouseLeave);
      }
    };
  }, [isHoverAvailable, handleMouseOver, handleMouseLeave, hidden]);

  return [ref, getIcon];
};
